import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export interface PageView {
  path: string;
  title: string;
  timestamp: number;
  referrer: string;
}

export interface AnalyticsData {
  pageViews: PageView[];
  totalVisits: number;
  uniquePages: Record<string, number>;
  dailyVisits: Record<string, number>;
  firstVisit: number;
}

const STORAGE_KEY = 'site_analytics_v1';
const MAX_VIEWS = 2000;

const PAGE_TITLES: Record<string, string> = {
  '/': 'Home',
  '/about': 'About Us',
  '/services': 'Services',
  '/revenue-sharing': 'Revenue Sharing',
  '/join': 'Join Us',
  '/contact': 'Contact',
  '/partnership': 'Partnership',
  '/privacy': 'Privacy Policy',
  '/terms': 'Terms of Service',
};

export const getAnalyticsData = (): AnalyticsData => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as AnalyticsData;
  } catch {
    // ignore
  }
  return { pageViews: [], totalVisits: 0, uniquePages: {}, dailyVisits: {}, firstVisit: Date.now() };
};

export const clearAnalyticsData = () => {
  localStorage.removeItem(STORAGE_KEY);
};

const saveAnalyticsData = (data: AnalyticsData) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
};

const trackPageView = (path: string) => {
  const data = getAnalyticsData();
  const today = new Date().toISOString().split('T')[0];
  const view: PageView = {
    path,
    title: PAGE_TITLES[path] || path,
    timestamp: Date.now(),
    referrer: document.referrer || 'direct',
  };

  data.pageViews = [view, ...data.pageViews].slice(0, MAX_VIEWS);
  data.totalVisits = (data.totalVisits || 0) + 1;
  data.uniquePages = { ...data.uniquePages, [path]: ((data.uniquePages || {})[path] || 0) + 1 };
  data.dailyVisits = { ...data.dailyVisits, [today]: ((data.dailyVisits || {})[today] || 0) + 1 };
  if (!data.firstVisit) data.firstVisit = Date.now();

  saveAnalyticsData(data);
};

const useAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Skip admin pages
    if (location.pathname.startsWith('/admin')) return;
    trackPageView(location.pathname);
  }, [location.pathname]);
};

export default useAnalytics;
